import {
    Component, EventEmitter, Output,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import {
    filter,
    firstValueFrom,
    startWith,
} from 'rxjs';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserService } from 'src/app/services/user.service';
import {
    ApolloService, getCollection_collections_by_pk,
} from 'src/gql-generated/generated';
import { ConnectionService } from 'src/app/services/connection.service';
import { DynamicDialogComponent } from '../../shared/dynamic-dialog/dynamic-dialog.component';
import { FileListComponent } from '../../shared/file-list/file-list.component';
import { NgIf, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { TooltipOnOverflowDirective } from '../../../directives/tooltip-on-overflow.directive';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

@Component({
    selector: 'app-collection-detail',
    templateUrl: './collection-detail.component.html',
    styleUrl: './collection-detail.component.scss',
    imports: [
        NgIf,
        MatIcon,
        MatIconButton,
        RouterLink,
        MatTooltip,
        TooltipOnOverflowDirective,
        MatButton,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        NgClass,
        FileListComponent,
        DynamicDialogComponent,
    ],
})
export class CollectionDetailComponent {
    /**
     * Reference to file list
     */
    @ViewChild('fileList') fileList?: FileListComponent;

    /**
     * Reference to the denyCandidateDialog
     * this reference is used to open the dialog and read the users actions
     */
    @ViewChild('denyCandidateDialog') denyCandidateDialog?: DynamicDialogComponent;

    /**
     * Emit event when collection was updated
     */
    @Output() collectionUpdated = new EventEmitter<void>();

    /**
     * Collection to display details for
     */
    collection?: getCollection_collections_by_pk;

    /**
     * File-list accepts a control as input. This control is used to provide the documents to display in the list via that control.
     */
    filesControl = new FormControl<
        {
            file: File;
            id?: string;
            status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry';
            subText?: string;
            created_at: Date;
        }[]>([], { nonNullable: true });

    /**
     * Amount of documents currently processing
     */
    documentsProcessing?: number;

    constructor(
        private route: ActivatedRoute,
        private apollo: ApolloService,
        private router: Router,
        private user: UserService,
        private snackbar: SnackbarService,
        public breakpoint: BreakpointService,
        private sentry: SentryService,
        public connection: ConnectionService,
    ) {
        // Subscribe to route changes to update collection to show
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            startWith(null),
            takeUntilDestroyed(),
        ).subscribe(() => {
            this.getCollection();
            // Reset paginator to first page on collection change
            if (this.fileList?.paginator) {
                this.fileList.paginator.pageIndex = 0;
            }
        });
    }


    /**
     * Get collection to display based on collectionId url parameter
     */
    async getCollection(): Promise<void> {
        const collectionId = this.route.snapshot.paramMap.get('collectionId') || undefined;
        if (collectionId) {
            const result = await firstValueFrom(this.apollo.getCollection({
                id: collectionId,
            }));

            if (result.data?.collections_by_pk) {
                this.collection = result.data.collections_by_pk;
                // Reflect collection's documents in the filesControl for file-list component
                this.filesControl.setValue(
                    this.collection.Documents.map((document) => ({
                        file: { name: document.File.name } as File,
                        id: document.File.id,
                        status: document.status as 'available' | 'processing' | 'failed' | 'queued' | 'retry' | undefined,
                        created_at: new Date(document.created_at),
                    })),
                );
                this.fileList?.switchOrder();
                this.documentsProcessing = this.collection.Documents.filter((document) => document.status === 'processing').length;
            }
        }
    }

    /**
     * Attempts to soft-delete a collection
     * @param dialog Dialog to ask user confirmation
     */
    async deleteCollection(dialog: DynamicDialogComponent): Promise<void> {
        if (this.collection) {
            try {
                const accepted = await dialog.open();
                if (accepted) {
                    const updateCollectionResult = await firstValueFrom(this.apollo.updateCollection({
                        id: this.collection.id,
                        updatedById: this.user.profile.id,
                        collectionUpdates: {
                            deleted_at: 'now()',
                            deleted_by_id: this.user.profile.id,
                        },
                    }));
                    if (updateCollectionResult.data?.update_collections_by_pk?.id) {
                        this.snackbar.success($localize`Collectie succesvol verwijderd`);
                        this.router.navigate(['/collections']);
                    }
                }
            } catch (error) {
                this.sentry.error(error);
                this.snackbar.error($localize`Collectie verwijderen mislukt`);
            }
        }
    }
}
